import { useState, useEffect } from 'react';
import { Truck, ChevronDown, ChevronUp, Shield } from 'lucide-react';

const STORAGE_KEY = 'crisis_vehicle_profile';

const PRESETS = [
  { name: 'Car', height: null, width: null, weight: null },
  { name: 'Campervan', height: 2.8, width: 2.1, weight: 3.5 },
  { name: 'Motorhome', height: 3.2, width: 2.3, weight: 5.0 },
  { name: 'Large RV', height: 3.5, width: 2.5, weight: 7.5 },
  { name: 'Transit Van', height: 2.5, width: 2.0, weight: 3.0 },
  { name: 'Custom', height: 0, width: 0, weight: 0 },
];

export const VehicleProfile = ({ onProfileChange, c1, c2 }) => {
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || { preset: 'Car', height: null, width: null, weight: null, fuelTank: 60, consumption: 8 }; }
    catch { return { preset: 'Car', height: null, width: null, weight: null, fuelTank: 60, consumption: 8 }; }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    onProfileChange?.(profile);
  }, [profile, onProfileChange]);

  const selectPreset = (p) => {
    setProfile(prev => ({ ...prev, preset: p.name, height: p.height, width: p.width, weight: p.weight }));
  };

  const isVan = profile.preset !== 'Car';

  return (
    <div className="rounded-lg border border-border/30 bg-card/80 overflow-hidden" data-testid="vehicle-profile">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-foreground hover:bg-muted/20 transition-colors"
        data-testid="vehicle-profile-toggle"
      >
        <span className="flex items-center gap-2">
          <Truck className="w-3.5 h-3.5" style={{ color: isVan ? c1 : 'inherit' }} />
          {isVan ? `${profile.preset} Mode` : 'Vehicle: Car (standard)'}
          {isVan && <Shield className="w-3 h-3 text-green-400" />}
        </span>
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2.5 border-t border-border/20 pt-2.5">
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map(p => (
              <button
                key={p.name}
                onClick={() => selectPreset(p)}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-colors ${
                  profile.preset === p.name ? 'text-white border-transparent' : 'bg-card border-border text-muted-foreground hover:text-foreground'
                }`}
                style={profile.preset === p.name ? { backgroundColor: c1 } : {}}
                data-testid={`vehicle-preset-${p.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                {p.name}
              </button>
            ))}
          </div>
          {isVan && (
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] text-muted-foreground">Height (m)</label>
                <input type="number" step="0.1" min="1" max="5" value={profile.height || ''}
                  onChange={e => setProfile(p => ({ ...p, height: parseFloat(e.target.value) || null, preset: 'Custom' }))}
                  className="w-full bg-muted/30 border border-border/40 rounded px-2 py-1 text-xs text-foreground"
                  data-testid="vehicle-height" />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">Width (m)</label>
                <input type="number" step="0.1" min="1" max="4" value={profile.width || ''}
                  onChange={e => setProfile(p => ({ ...p, width: parseFloat(e.target.value) || null, preset: 'Custom' }))}
                  className="w-full bg-muted/30 border border-border/40 rounded px-2 py-1 text-xs text-foreground"
                  data-testid="vehicle-width" />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">Weight (t)</label>
                <input type="number" step="0.5" min="1" max="20" value={profile.weight || ''}
                  onChange={e => setProfile(p => ({ ...p, weight: parseFloat(e.target.value) || null, preset: 'Custom' }))}
                  className="w-full bg-muted/30 border border-border/40 rounded px-2 py-1 text-xs text-foreground"
                  data-testid="vehicle-weight" />
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 pt-1 border-t border-border/20">
            <div>
              <label className="text-[10px] text-muted-foreground">Tank (litres)</label>
              <input type="number" step="5" min="10" max="200" value={profile.fuelTank || 60}
                onChange={e => setProfile(p => ({ ...p, fuelTank: parseInt(e.target.value) || 60 }))}
                className="w-full bg-muted/30 border border-border/40 rounded px-2 py-1 text-xs text-foreground"
                data-testid="fuel-tank" />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground">L/100km</label>
              <input type="number" step="0.5" min="3" max="30" value={profile.consumption || 8}
                onChange={e => setProfile(p => ({ ...p, consumption: parseFloat(e.target.value) || 8 }))}
                className="w-full bg-muted/30 border border-border/40 rounded px-2 py-1 text-xs text-foreground"
                data-testid="fuel-consumption" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
